import React, { useState } from 'react';
import {
  FiCalendar, FiClock, FiShoppingCart, FiChevronDown, FiChevronUp,
  FiCopy, FiCheck, FiX
} from 'react-icons/fi';
import './MealPlanViewer.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MEAL_TYPES_ORDER = ['Breakfast', 'Lunch', 'Dinner', 'Snack 1', 'Snack 2'];

const MealPlanViewer = ({ mealPlan, onLogMeal, onClose }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [expandedMeals, setExpandedMeals] = useState({});
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [copiedMeals, setCopiedMeals] = useState(new Set());

  if (!mealPlan) {
    return (
      <div className="meal-plan-viewer">
        <div className="no-meal-plan">
          <FiCalendar size={48} />
          <h3>No Meal Plan Assigned</h3>
          <p>Your trainer hasn't assigned a meal plan yet.</p>
        </div>
      </div>
    );
  }

  const toggleMealExpanded = (day, mealType) => {
    const key = `${day}-${mealType}`;
    setExpandedMeals(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isMealExpanded = (day, mealType) => {
    const key = `${day}-${mealType}`;
    return expandedMeals[key] || false;
  };

  const calculateDayTotals = (dayMeals) => {
    if (!dayMeals) return { calories: 0, protein: 0, carbs: 0, fat: 0 };

    return Object.values(dayMeals).reduce((totals, foods) => {
      if (!Array.isArray(foods)) return totals;

      const mealTotals = foods.reduce((acc, food) => ({
        calories: acc.calories + (food.calories || 0),
        protein: acc.protein + (food.protein || 0),
        carbs: acc.carbs + (food.carbs || 0),
        fat: acc.fat + (food.fat || 0)
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      return {
        calories: totals.calories + mealTotals.calories,
        protein: totals.protein + mealTotals.protein,
        carbs: totals.carbs + mealTotals.carbs,
        fat: totals.fat + mealTotals.fat
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  const calculateMealTotals = (foods) => {
    if (!Array.isArray(foods) || foods.length === 0) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    }

    return foods.reduce((totals, food) => ({
      calories: totals.calories + (food.calories || 0),
      protein: totals.protein + (food.protein || 0),
      carbs: totals.carbs + (food.carbs || 0),
      fat: totals.fat + (food.fat || 0)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  const handleCopyMeal = (day, mealType, foods) => {
    const key = `${day}-${mealType}`;

    // Add visual feedback
    setCopiedMeals(prev => new Set(prev).add(key));

    // Call the onLogMeal callback if provided
    if (onLogMeal) {
      onLogMeal({ day, mealType, foods });
    }

    // Remove feedback after 2 seconds
    setTimeout(() => {
      setCopiedMeals(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }, 2000);
  };

  const isMealCopied = (day, mealType) => {
    return copiedMeals.has(`${day}-${mealType}`);
  };

  const generateShoppingList = () => {
    const items = {};

    if (!mealPlan.meals) return [];

    // Aggregate all foods across all days and meals
    DAYS_OF_WEEK.forEach(day => {
      const dayMeals = mealPlan.meals[day];
      if (!dayMeals) return;

      Object.entries(dayMeals).forEach(([mealType, foods]) => {
        if (!Array.isArray(foods)) return;

        foods.forEach(food => {
          const key = food.name;
          if (!items[key]) {
            items[key] = {
              name: food.name,
              quantity: food.quantity || 1,
              unit: food.unit || 'serving',
              count: 1
            };
          } else {
            items[key].count += 1;
          }
        });
      });
    });

    return Object.values(items).sort((a, b) => a.name.localeCompare(b.name));
  };

  const shoppingList = generateShoppingList();

  const renderMealCard = (day, mealType, foods) => {
    if (!foods || foods.length === 0) return null;

    const totals = calculateMealTotals(foods);
    const isExpanded = isMealExpanded(day, mealType);
    const isCopied = isMealCopied(day, mealType);

    return (
      <div key={mealType} className="meal-card">
        <div className="meal-card-header" onClick={() => toggleMealExpanded(day, mealType)}>
          <div className="meal-type-info">
            <h4>{mealType}</h4>
            <span className="meal-item-count">{foods.length} items</span>
          </div>
          <div className="meal-card-actions">
            <span className="meal-calories">{Math.round(totals.calories)} cal</span>
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </div>
        </div>

        {isExpanded && (
          <div className="meal-card-body">
            <div className="meal-foods-list">
              {foods.map((food, idx) => (
                <div key={idx} className="meal-food-item">
                  <div className="food-item-info">
                    <strong>{food.name}</strong>
                    <span className="food-quantity">{food.quantity || '1 serving'}</span>
                  </div>
                  <div className="food-macros">
                    <span>{Math.round(food.calories || 0)}cal</span>
                    <span>P:{Math.round(food.protein || 0)}g</span>
                    <span>C:{Math.round(food.carbs || 0)}g</span>
                    <span>F:{Math.round(food.fat || 0)}g</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="meal-totals">
              <strong>Totals:</strong>
              <div className="totals-row">
                <span>{Math.round(totals.calories)} cal</span>
                <span>P: {Math.round(totals.protein)}g</span>
                <span>C: {Math.round(totals.carbs)}g</span>
                <span>F: {Math.round(totals.fat)}g</span>
              </div>
            </div>

            <button
              className={`copy-meal-btn ${isCopied ? 'copied' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleCopyMeal(day, mealType, foods);
              }}
            >
              {isCopied ? (
                <>
                  <FiCheck /> Copied to Logger
                </>
              ) : (
                <>
                  <FiCopy /> Copy to Food Logger
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderDayView = (day) => {
    const dayMeals = mealPlan.meals?.[day] || {};
    const dayTotals = calculateDayTotals(dayMeals);

    return (
      <div className="day-view">
        <div className="day-header">
          <h3>{day}</h3>
          <div className="day-totals">
            <span className="total-calories">{Math.round(dayTotals.calories)} cal</span>
            <span className="total-macros">
              P:{Math.round(dayTotals.protein)}g •
              C:{Math.round(dayTotals.carbs)}g •
              F:{Math.round(dayTotals.fat)}g
            </span>
          </div>
        </div>

        <div className="meals-list">
          {MEAL_TYPES_ORDER.map(mealType => {
            const foods = dayMeals[mealType];
            if (!foods || foods.length === 0) return null;
            return renderMealCard(day, mealType, foods);
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    return (
      <div className="week-view">
        <div className="week-grid">
          {DAYS_OF_WEEK.map(day => {
            const dayMeals = mealPlan.meals?.[day] || {};
            const dayTotals = calculateDayTotals(dayMeals);
            const hasMeals = Object.values(dayMeals).some(meals =>
              Array.isArray(meals) && meals.length > 0
            );

            return (
              <div
                key={day}
                className={`day-card ${!hasMeals ? 'empty' : ''} ${selectedDay === day ? 'selected' : ''}`}
                onClick={() => setSelectedDay(day)}
              >
                <div className="day-card-header">
                  <h4>{day}</h4>
                  <FiCalendar />
                </div>

                {hasMeals ? (
                  <>
                    <div className="day-card-calories">
                      {Math.round(dayTotals.calories)} cal
                    </div>
                    <div className="day-card-meals">
                      {MEAL_TYPES_ORDER.map(mealType => {
                        const foods = dayMeals[mealType];
                        if (!foods || foods.length === 0) return null;
                        return (
                          <div key={mealType} className="meal-indicator">
                            <div className="meal-dot"></div>
                            <span>{mealType}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="no-meals-indicator">No meals planned</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="meal-plan-viewer">
      {/* Header */}
      <div className="viewer-header">
        <div className="header-left">
          <h2>Weekly Meal Plan</h2>
          <p>
            {mealPlan.name || 'Custom Meal Plan'}
            {mealPlan.clientName && ` • Assigned by ${mealPlan.clientName}`}
          </p>
        </div>
        <div className="header-actions">
          <button className="shopping-list-btn" onClick={() => setShowShoppingList(true)}>
            <FiShoppingCart />
            Shopping List
          </button>
          {onClose && (
            <button className="close-btn" onClick={onClose}>
              <FiX />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {selectedDay ? renderDayView(selectedDay) : renderWeekView()}

      {/* Navigation */}
      {selectedDay && (
        <button className="back-to-week-btn" onClick={() => setSelectedDay(null)}>
          ← Back to Week View
        </button>
      )}

      {/* Shopping List Modal */}
      {showShoppingList && (
        <div className="modal-overlay" onClick={() => setShowShoppingList(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FiShoppingCart /> Shopping List
              </h3>
              <button className="modal-close" onClick={() => setShowShoppingList(false)}>
                <FiX />
              </button>
            </div>

            <div className="shopping-list-content">
              {shoppingList.length === 0 ? (
                <p className="empty-list">No items in your meal plan</p>
              ) : (
                <>
                  <p className="list-description">
                    Items needed for your weekly meal plan ({shoppingList.length} unique items)
                  </p>
                  <div className="shopping-items">
                    {shoppingList.map((item, idx) => (
                      <div key={idx} className="shopping-item">
                        <div className="item-checkbox">
                          <input type="checkbox" id={`item-${idx}`} />
                          <label htmlFor={`item-${idx}`}></label>
                        </div>
                        <div className="item-info">
                          <strong>{item.name}</strong>
                          <span className="item-quantity">
                            {item.count > 1 && `${item.count}x needed`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="print-btn" onClick={() => window.print()}>
                Print List
              </button>
              <button className="close-modal-btn" onClick={() => setShowShoppingList(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPlanViewer;
